/**
 * Detects that a newer build has been deployed while this tab has been open.
 *
 * Why this is needed: nothing in normal use reloads the page. React Router
 * navigation is entirely client-side, and sign-in uses signInWithPopup, so the
 * document is never re-fetched. A tab opened on Saturday keeps running
 * Saturday's bundle all through Sunday no matter how many screens the operator
 * visits — which is exactly the kind of machine that runs a check-in desk.
 *
 * The mechanism: `scripts/bump-version.sh` writes the new version into both
 * src/version.js (compiled into the bundle) and public/version.json (shipped
 * as a static file). The running app polls the static file; when the two
 * disagree, newer code exists on the server.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import APP_VERSION from '../version';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 60 * 1000;      // 1 minute
// Only a short pause is required: long enough not to reload under someone who
// is mid-search, short enough that a desk in steady use still picks up a deploy
// within the service instead of running stale code alongside updated tabs.
const QUIET_BEFORE_AUTO_RELOAD_MS = 15 * 1000;
const AUTO_RELOAD_FLAG = 'btagms:autoReloadedForVersion';

// ---------------------------------------------------------------------------
// Busy registry
// ---------------------------------------------------------------------------

// Screens register themselves here while they hold work the operator would
// lose on a reload — an in-flight check-in submit, or a selection that has not
// been submitted yet. The auto-reload below refuses to fire while any key is
// held. The manual "Reload" button ignores this: that one is the operator's
// own decision.
const busyKeys = new Set();

/**
 * Mark a screen as holding unsaved work (or release it).
 * @param {string} key - stable identifier for the screen
 * @param {boolean} busy
 */
export const setAppBusy = (key, busy) => {
  if (busy) busyKeys.add(key);
  else busyKeys.delete(key);
};

export const isAppBusy = () => busyKeys.size > 0;

/**
 * React helper: keeps the registry in sync with a component's own state and
 * clears the key on unmount, so a screen that navigates away mid-edit cannot
 * leave the app permanently marked busy.
 */
export const useAppBusy = (key, busy) => {
  useEffect(() => {
    setAppBusy(key, busy);
    return () => setAppBusy(key, false);
  }, [key, busy]);
};

// ---------------------------------------------------------------------------
// Version polling
// ---------------------------------------------------------------------------

/**
 * @returns {{updateAvailable: boolean, latestVersion: string|null, currentVersion: string, reload: Function}}
 */
export const useAppUpdate = () => {
  const [latestVersion, setLatestVersion] = useState(null);
  const lastInteractionRef = useRef(Date.now());

  const check = useCallback(async () => {
    // Always attempt the fetch, even when navigator.onLine is false. Chrome can
    // report offline while the network works (VPN adapters, waking from
    // sleep), and skipping on that left tabs stuck on an old bundle. A real
    // offline fetch just fails and is swallowed below.
    try {
      // no-store so this never reads the browser's own cache — the whole point
      // is to ask the server what it currently has.
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;

      const data = await res.json();
      if (data?.version) setLatestVersion(data.version);
    } catch {
      // Offline, blocked, or a transient failure. Nothing to do — the next
      // poll will pick it up, and a missed check must never surface to a user.
    }
  }, []);

  // Poll on an interval, and opportunistically whenever the tab is brought
  // back to the foreground — that is when a kiosk left open overnight is most
  // likely to be looked at again.
  useEffect(() => {
    check();

    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    window.addEventListener('online', check);

    return () => {
      clearInterval(interval);
      window.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.removeEventListener('online', check);
    };
  }, [check]);

  // Track interaction so the auto-reload below can tell an idle kiosk from
  // someone mid-task.
  useEffect(() => {
    const touch = () => { lastInteractionRef.current = Date.now(); };
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach(e => window.addEventListener(e, touch, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, touch));
  }, []);

  const updateAvailable = Boolean(latestVersion && latestVersion !== APP_VERSION);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  // Auto-reload as soon as it is safe: an update is available, no screen holds
  // unsaved work, and the operator has paused for a few seconds. Every signed-in
  // tab should be on the deployed code — a tab left on an old bundle behaves
  // differently from the others and confuses whoever is using it. The Firebase
  // session persists across the reload, so the operator stays signed in. The
  // busy registry is what keeps this from interrupting a check-in in progress.
  useEffect(() => {
    if (!updateAvailable) return;

    // One automatic reload per version per tab. If the new bundle somehow
    // still reports the old version, this stops it from looping.
    try {
      if (sessionStorage.getItem(AUTO_RELOAD_FLAG) === latestVersion) return;
    } catch {
      // sessionStorage unavailable (private mode); fall through and rely on
      // the idle + busy guards alone.
    }

    const timer = setInterval(() => {
      if (isAppBusy()) return;
      if (Date.now() - lastInteractionRef.current < QUIET_BEFORE_AUTO_RELOAD_MS) return;

      try {
        sessionStorage.setItem(AUTO_RELOAD_FLAG, latestVersion);
      } catch {
        // Not fatal — proceed with the reload.
      }
      window.location.reload();
    }, 5000);

    return () => clearInterval(timer);
  }, [updateAvailable, latestVersion]);

  return { updateAvailable, latestVersion, currentVersion: APP_VERSION, reload };
};
